<template>
  <div class="image-upload">
    <!-- 图片预览区域 -->
    <div class="upload-area" @click="triggerUpload" v-if="!previewUrl">
      <el-icon class="upload-icon"><Plus /></el-icon>
      <div class="upload-text">{{ placeholder }}</div>
    </div>
    <div class="preview-container" v-else>
      <img :src="previewUrl" class="preview-image" @click="triggerUpload" />
      <div class="preview-actions">
        <el-button type="primary" size="small" @click="triggerUpload">更换</el-button>
        <el-button type="danger" size="small" @click="clearImage">删除</el-button>
      </div>
    </div>
    
    <!-- 隐藏的文件输入 -->
    <input 
      ref="fileInput" 
      type="file" 
      accept="image/*" 
      style="display: none" 
      @change="handleFileChange" 
    />
    
    <!-- 裁剪对话框 -->
    <el-dialog 
      v-model="cropDialogVisible" 
      title="图片裁剪" 
      width="800px"
      :close-on-click-modal="false"
    >
      <div class="cropper-container">
        <VueCropper
          ref="cropperRef"
          :img="cropImageSrc"
          :output-size="1"
          :output-type="'jpeg'"
          :info="true"
          :full="false"
          :can-move="true"
          :can-move-box="true"
          :fixed-box="false"
          :original="false"
          :auto-crop="true"
          :auto-crop-width="200"
          :auto-crop-height="200"
          :center-box="true"
          :high="true"
          :fixed="true"
          :fixed-number="[1, 1]"
          :max-img-size="3000"
        />
      </div>
      <template #footer>
        <el-button @click="cropDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmCrop">确认裁剪</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { VueCropper } from 'vue-cropper'
import 'vue-cropper/dist/index.css'
import imageConversion from 'image-conversion'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '点击上传图片'
  },
  maxSize: {
    type: Number,
    default: 150 // KB
  },
  enableCrop: {
    type: Boolean,
    default: false
  },
  aspectRatio: {
    type: Number,
    default: 1 // 1:1 比例，用于logo
  }
})

const emit = defineEmits(['update:modelValue'])

const fileInput = ref(null)
const cropperRef = ref(null)
const previewUrl = ref(props.modelValue || '')
const cropDialogVisible = ref(false)
const cropImageSrc = ref('')

// 监听外部值变化
watch(() => props.modelValue, (newVal) => {
  previewUrl.value = newVal || ''
})

// 触发文件选择
const triggerUpload = () => {
  fileInput.value?.click()
}

// 清除图片
const clearImage = () => {
  previewUrl.value = ''
  emit('update:modelValue', '')
}

// 处理文件选择
const handleFileChange = async (e) => {
  const file = e.target.files?.[0]
  if (!file) return
  
  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    ElMessage.error('请选择图片文件')
    return
  }
  
  // 重置input
  e.target.value = ''
  
  // 读取文件
  const reader = new FileReader()
  reader.onload = async (event) => {
    const dataUrl = event.target.result
    
    if (props.enableCrop) {
      // 需要裁剪
      cropImageSrc.value = dataUrl
      cropDialogVisible.value = true
    } else {
      // 直接压缩上传
      await processAndUpload(dataUrl, file)
    }
  }
  reader.readAsDataURL(file)
}

// 确认裁剪
const confirmCrop = async () => {
  if (!cropperRef.value) return
  
  // vue-cropper 的获取裁剪图片方法
  cropperRef.value.getCropBlob(async (blob) => {
    if (!blob) {
      ElMessage.error('裁剪失败')
      return
    }
    
    cropDialogVisible.value = false
    
    // 转换为base64
    const reader = new FileReader()
    reader.onload = async (e) => {
      await processAndUpload(e.target.result, null)
    }
    reader.readAsDataURL(blob)
  })
}

// 处理并上传图片
const processAndUpload = async (dataUrl, originalFile) => {
  try {
    // 压缩图片
    let compressedDataUrl = await compressImage(dataUrl, props.maxSize)
    
    // 这里暂时使用base64，实际项目中应该上传到服务器
    // TODO: 实现服务器上传
    previewUrl.value = compressedDataUrl
    emit('update:modelValue', compressedDataUrl)
    
    ElMessage.success('图片处理成功')
  } catch (error) {
    console.error('图片处理失败:', error)
    ElMessage.error('图片处理失败')
  }
}

// 压缩图片到指定大小（强制压缩）
const compressImage = async (dataUrl, maxSizeKB) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = async () => {
      try {
        // 如果图片太大，先缩小尺寸
        let width = img.width
        let height = img.height
        const maxDimension = 1200 // 最大边长
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round(height * maxDimension / width)
            width = maxDimension
          } else {
            width = Math.round(width * maxDimension / height)
            height = maxDimension
          }
        }
        
        // 创建canvas缩小图片
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        // 转为blob
        const resizeBlob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/jpeg', 0.9)
        })
        
        // 使用image-conversion精确压缩到目标大小
        const compressedBlob = await imageConversion.compressAccurately(resizeBlob, {
          size: maxSizeKB,
          accuracy: 0.95,  // 95%准确度
          type: 'image/jpeg'
        })
        
        // 检查压缩后大小
        console.log(`图片压缩完成: ${(compressedBlob.size / 1024).toFixed(1)}KB`)
        
        // 转回base64
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(compressedBlob)
      } catch (error) {
        console.error('图片压缩失败:', error)
        // 压缩失败时尝试简单的质量压缩
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)
          const fallbackDataUrl = canvas.toDataURL('image/jpeg', 0.5)
          console.log('使用降级压缩')
          resolve(fallbackDataUrl)
        } catch (e) {
          reject(e)
        }
      }
    }
    img.onerror = () => reject(new Error('图片加载失败'))
    img.src = dataUrl
  })
}
</script>

<style scoped>
.image-upload {
  display: inline-block;
}

.upload-area {
  width: 120px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.3s;
  background: #fafafa;
}

.upload-area:hover {
  border-color: #409eff;
}

.upload-icon {
  font-size: 28px;
  color: #8c939d;
}

.upload-text {
  font-size: 12px;
  color: #8c939d;
  margin-top: 8px;
}

.preview-container {
  position: relative;
  width: 120px;
}

.preview-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid #d9d9d9;
}

.preview-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 6px 6px;
}

.cropper-container {
  height: 400px;
}
</style>
